/**
 * Avatar URL utilities - PRODUCTION-READY IMPLEMENTATION
 * Single source of truth for avatar display with robust cache-busting
 * 
 * Backend provides:
 * - avatarUrl: Canonical path (e.g., "/api/avatars/{gridfs_id}")
 * - avatar_gridfs_id: MongoDB GridFS file ID (persistent storage)
 * - avatar_timestamp: Unix timestamp in milliseconds for cache-busting
 * - avatar_updated_at: ISO datetime (fallback)
 * 
 * NOTE: Avatars are stored in MongoDB GridFS for persistence across deploys.
 * Old /api/uploads/avatars/ URLs are automatically migrated on startup.
 * 
 * This utility ensures:
 * 1. Instant avatar updates after upload (no manual refresh)
 * 2. Consistent display across all pages
 * 3. Graceful fallback to initials when no avatar exists
 * 4. Handles both new GridFS URLs and legacy filesystem URLs
 */

// Use relative URLs for avatar paths (works with proxy in dev, same-origin in production)
const BACKEND_URL = '';

/**
 * Get complete avatar URL with cache-busting parameters
 * 
 * @param {Object} user - User/reviewer object from API
 * @param {string} user.avatarUrl - Base avatar path (canonical field)
 * @param {string} user.avatar - Legacy avatar field (fallback)
 * @param {number} user.avatar_timestamp - Unix timestamp in ms (preferred for cache-busting)
 * @param {string} user.avatar_updated_at - ISO datetime (fallback for cache-busting)
 * 
 * @returns {string|null} Complete avatar URL with cache-busting, or null if no avatar
 * 
 * Example output: "/api/avatars/694043267068cedbf6a89d69?v=1702500000000"
 */
export const getAvatarUrl = (user) => {
  if (!user) return null;
  
  // Get avatar URL (prefer canonical avatarUrl, fallback to legacy avatar field)
  const avatarPath = user.avatarUrl || user.avatar;
  
  // Robust null/undefined handling
  if (!avatarPath || avatarPath === 'null' || avatarPath === 'undefined' || avatarPath === '') {
    return null;
  }

  // BLOCK LEGACY PATHS: /api/uploads/avatars/ URLs are no longer valid
  // These paths don't persist across redeploys - reject them completely
  if (avatarPath.includes('/api/uploads/avatars/')) {
    console.warn('[avatarUtils] Rejecting legacy avatar path:', avatarPath);
    return null; // Force fallback to initials
  }

  // Build full URL if not already absolute
  let fullUrl = avatarPath.startsWith('http') 
    ? avatarPath 
    : `${BACKEND_URL}${avatarPath}`;

  // Get timestamp for cache-busting
  let timestamp;
  
  // Prefer avatar_timestamp (millisecond precision from backend)
  if (user.avatar_timestamp && typeof user.avatar_timestamp === 'number') {
    timestamp = user.avatar_timestamp;
  } 
  // Fallback to parsing avatar_updated_at datetime string
  else if (user.avatar_updated_at) {
    try {
      const parsed = typeof user.avatar_updated_at === 'string' 
        ? new Date(user.avatar_updated_at).getTime() 
        : user.avatar_updated_at.getTime?.() || Date.now();
      timestamp = parsed;
    } catch (e) {
      console.warn('[avatarUtils] Failed to parse avatar_updated_at:', e);
      timestamp = Date.now(); // Last resort: current time
    }
  }
  // No timestamp available: use current time (ensures fresh load)
  else {
    timestamp = Date.now();
  }
  
  // Add cache-busting query parameter
  return `${fullUrl}?v=${timestamp}`;
};

/**
 * Get initials from name for fallback avatar badge
 * 
 * @param {string} name - User's full name
 * @returns {string} Two-letter initials in uppercase
 * 
 * Examples:
 * - "John Doe" → "JD"
 * - "Prince" → "PR"
 * - "" → "??"
 */
export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '??';
  
  const parts = name.trim().split(/\s+/);
  
  if (parts.length === 0 || parts[0] === '') {
    return '??';
  }
  
  if (parts.length === 1) {
    // Single word: use first two characters
    const word = parts[0];
    return word.length >= 2 
      ? word.substring(0, 2).toUpperCase()
      : (word.charAt(0) + word.charAt(0)).toUpperCase();
  }
  
  // Multiple words: use first character of first and last word
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Get complete avatar data for a reviewer with fallback handling
 * 
 * @param {Object} reviewer - Reviewer object from API
 * @returns {Object} Avatar data with display helpers
 * @returns {string|null} returns.avatarUrl - Full URL with cache-busting, or null
 * @returns {string} returns.initials - Two-letter initials for fallback
 * @returns {boolean} returns.hasAvatar - Whether user has an avatar image
 * 
 * Usage in components:
 * ```jsx
 * const { avatarUrl, initials, hasAvatar } = getAvatarData(reviewer);
 * 
 * {hasAvatar ? (
 *   <img src={avatarUrl} alt={reviewer.name} />
 * ) : (
 *   <div className="initials-badge">{initials}</div>
 * )}
 * ```
 */
export const getAvatarData = (reviewer) => {
  if (!reviewer) {
    return { 
      avatarUrl: null, 
      initials: '??', 
      hasAvatar: false 
    };
  }

  const avatarUrl = getAvatarUrl(reviewer);
  const initials = getInitials(reviewer.name || reviewer.username);
  
  return {
    avatarUrl,
    initials,
    hasAvatar: Boolean(avatarUrl)
  };
};

/**
 * Force refresh avatar by dispatching custom event
 * Call this after avatar upload to update all components
 * 
 * @param {number} timestamp - New avatar timestamp
 * 
 * Usage after avatar upload:
 * ```jsx
 * const response = await uploadAvatar(file);
 * refreshAvatar(response.avatar_timestamp);
 * ```
 */
export const refreshAvatar = (timestamp) => {
  const event = new CustomEvent('avatarUpdate', { 
    detail: { timestamp: timestamp || Date.now() } 
  });
  window.dispatchEvent(event);
};

/**
 * Hook to listen for avatar updates
 * Use in components that display avatars to auto-refresh
 * 
 * @returns {number} Update trigger value that changes on avatar update
 * 
 * Usage in components:
 * ```jsx
 * const avatarVersion = useAvatarRefresh();
 * // Component will re-render when avatar updates
 * ```
 */
export const useAvatarRefresh = () => {
  const [trigger, setTrigger] = React.useState(0);
  
  React.useEffect(() => {
    const handler = (e) => {
      setTrigger(e.detail?.timestamp || Date.now());
    };
    
    window.addEventListener('avatarUpdate', handler);
    return () => window.removeEventListener('avatarUpdate', handler);
  }, []);
  
  return trigger;
};

/**
 * Clean legacy avatar URLs from localStorage user object
 * Call this on app startup or after login to remove stale /api/uploads/ paths
 * 
 * This ensures users don't see 404 errors from cached legacy paths
 */
export const cleanLegacyAvatarCache = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    
    const user = JSON.parse(userStr);
    let cleaned = false;
    
    // Check for legacy avatar URLs
    if (user.avatarUrl && user.avatarUrl.includes('/api/uploads/avatars/')) {
      console.log('[avatarUtils] Cleaning legacy avatarUrl from localStorage');
      delete user.avatarUrl;
      cleaned = true;
    }
    
    if (user.avatar && user.avatar.includes('/api/uploads/avatars/')) {
      console.log('[avatarUtils] Cleaning legacy avatar from localStorage');
      delete user.avatar;
      cleaned = true;
    }
    
    // Also clean any legacy profileImageUrl field
    if (user.profileImageUrl && user.profileImageUrl.includes('/api/uploads/')) {
      delete user.profileImageUrl;
      cleaned = true;
    }
    
    if (cleaned) {
      localStorage.setItem('user', JSON.stringify(user));
      console.log('[avatarUtils] ✅ Cleaned legacy avatar URLs from localStorage');
    }
    
    return cleaned;
  } catch (e) {
    console.warn('[avatarUtils] Error cleaning localStorage:', e);
    return false;
  }
};

/**
 * Sanitize user object by removing legacy avatar paths
 * Use when processing user data from any source
 * 
 * @param {Object} user - User object to sanitize
 * @returns {Object} Sanitized user object
 */
export const sanitizeUserAvatarPaths = (user) => {
  if (!user) return user;
  
  const sanitized = { ...user };
  
  // Remove legacy avatar paths - they won't work after redeploy
  if (sanitized.avatarUrl && sanitized.avatarUrl.includes('/api/uploads/avatars/')) {
    sanitized.avatarUrl = null;
  }
  
  if (sanitized.avatar && sanitized.avatar.includes('/api/uploads/avatars/')) {
    sanitized.avatar = null;
  }
  
  return sanitized;
};

export default {
  getAvatarUrl,
  getInitials,
  getAvatarData,
  refreshAvatar,
  useAvatarRefresh,
  cleanLegacyAvatarCache,
  sanitizeUserAvatarPaths
};
